import { UniButton } from '@/design-system/components/buttons';
import { H6, TextBody, TextCaption, TextTitle } from '@/design-system/components/typography';
import { SxProp, View, useDripsyTheme, styled } from 'dripsy';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/design-system/components/icons/Icon';
import { Row } from '@/design-system/components/layout';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { WithdrawStackParamList } from '@/features/account-management/screens/withdraw/type';
import moment from 'moment-timezone';
import { PageHeaderModalNative } from '@/features/account-management/screens/page/PageHeaderModalNative';
import { formatAmountByDecimal } from '@/features/account-management/utils';

export interface WithdrawProps {
  onClose: () => void;
  data: any;
  returnData?: any;
  refresh?: () => void;
  record?: any;
}

export const WithdrawNativePlacedScreen: React.FC<WithdrawProps> = (props) => {
  const { onClose, data, returnData, refresh, record } = props;
  const { t } = useTranslation('account-management');
  const {
    topHeader,
    body,
    horizontalWrapper,
    textLabel,
    textDebitAmount,
    textTitle,
    textAtmPin,
    btnView,
    btn,
    textHeaderTitle,
    textHeaderSubTitle,
    modal
  } = sxStyles;

  const { theme } = useDripsyTheme();

  const navigation = useNavigation<NativeStackNavigationProp<WithdrawStackParamList>>();

  const [instructionTime, setInstructionTime] = useState(moment().format('YYYY-MM-DD HH:mm:ss'));

  return (
    <View sx={{ height: '100%', backgroundColor: '$white' }}>
      <PageHeaderModalNative>
        <Row sx={{ justifyContent: 'center', alignItems: 'center', px: '$4', mt: '$5', mb: '$4' }}>
          <View sx={{ textAlign: 'center' }}>
            <TextTitle bold sx={{ color: theme?.colors?.$white as string }}>
              {t('card-account-management::withdraw-form.title--withdraw')}
            </TextTitle>
          </View>
        </Row>
      </PageHeaderModalNative>
      <View sx={modal}>
        <View sx={topHeader}>
          <Icon name="checkCircleBlue" height={108} width={108} />
          <H6 bold sx={textHeaderTitle}>
            {t('card-account-management::withdraw-placed.title--withdraw-account-placed')}
          </H6>
          <TextCaption sx={textHeaderSubTitle}>{t('card-account-management::withdraw-placed.text--you-have-withdrawn')}</TextCaption>
        </View>
        <View sx={body}>
          <View sx={{ gap: '$5' }}>
            <View>
              <TextCaption sx={textTitle} semiBold>
                {t('card-account-management::glossary.from-account')}
              </TextCaption>
              <H6 sx={{ ...textAtmPin, mb: '$1' }}>
                {t('card-account-management::glossary.card-account')} - {record?.name}
              </H6>
            </View>
            <View>
              <TextCaption sx={textTitle} semiBold>
                {t('card-account-management::glossary.to-account')}
              </TextCaption>
              <H6 sx={{ ...textAtmPin, mb: '$1' }}>{data.wallet.label}</H6>
              <TextBody>{data.wallet.accountNumber}</TextBody>
            </View>
            <View>
              <TextCaption sx={textTitle} semiBold>
                {t('card-account-management::glossary.amount')}
              </TextCaption>
              <H6 sx={textAtmPin}>
                {formatAmountByDecimal({ value: data.amount, decimals: 2 })} {record.currency}
              </H6>
            </View>
            <View>
              <TextCaption sx={textTitle} bold>
                {t('card-account-management::glossary.xmetadata')}
              </TextCaption>
              {Object.keys(data.xmetadata).map((k) => (
                <View sx={{ pl: '$5', my: '$3' }}>
                  <TextCaption sx={textTitle} semiBold>
                    {k}
                  </TextCaption>
                  <H6 sx={textAtmPin}>{data.xmetadata[k]}</H6>
                </View>
              ))}
            </View>
            <View sx={horizontalWrapper}>
              <TextBody sx={textLabel}>{t('card-account-management::glossary.instruction-time')}</TextBody>
              <TextBody sx={textDebitAmount}>{returnData?.instructionTime || instructionTime || '-'}</TextBody>
            </View>
          </View>
          <View sx={btnView}>
            <UniButton
              sx={btn}
              labelSx={{ color: '$balck' }}
              label={t('card-account-management::general.button--close')}
              onPress={() => {
                refresh && refresh();
                navigation.getParent()?.goBack();
              }}
            />
          </View>
        </View>
      </View>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  modal: {
    padding: '$5'
  },
  topHeader: {
    justifyContent: 'center',
    alignItems: 'center',
    background: 'primaryDefault'
  },
  body: {
    py: 28
  },
  horizontalWrapper: {
    flexDirection: 'column',
    alignItems: 'flex-start'
  },
  textLabel: {
    color: '$textSecondary',
    mb: '$1'
  },
  textDebitAmount: {
    mb: '$1',
    color: '$textPrimary'
  },
  textTitle: {
    marginBottom: 4,
    color: '$textSecondary'
  },
  textAtmPin: {
    fontWeight: 'bolder',
    color: '$textThemeSecondary'
  },
  btnView: {
    flex: 1,
    display: 'grid',
    gap: '$3',
    mt: '$4'
  },
  btn: {
    width: '100%',
    height: 44
  },
  textHeaderTitle: {
    mb: '$2',
    textAlign: 'center'
  },
  textHeaderSubTitle: {
    color: '$textPrimary'
  },
  timeView: {
    mb: '$3',
    mt: '$5'
  },
  refView: {
    mb: '$10'
  }
};
